import { useCallback, useEffect, useState } from "react";
import {
  api,
  type MissionOverallProgressStatus,
  type MissionPlanDecisionKind,
  type MissionPlanReviewView,
  type MissionProgressView,
  type MissionStepProgressStatus,
  type MissionSummaryView,
} from "../api";

/**
 * SIMO-MISSION-PLAN-REVIEW-AND-APPROVAL-1 (B01 Slice 3).
 *
 * Der Owner sieht VOR jeder Ausfuehrung, was SIMO tun moechte, und
 * entscheidet — Freigabe, Ablehnung, Ueberarbeitung anfordern oder Stoppen.
 * Eine Entscheidung loest in diesem Slice NIE eine Ausfuehrung aus (siehe
 * `apps/server/src/mission-plan-approval.ts`); dieses Panel zeigt deshalb
 * bewusst nie "wird erledigt"/"laeuft", sondern "wartet auf Freigabe" /
 * "voraussichtlicher Schritt". Es gibt bewusst KEINE Schaltflaeche
 * "Alles automatisch erlauben".
 *
 * Selbststaendig datenholend, gleiches Muster wie `OwnerRunConsole.tsx`:
 * eigenes Laden, eigener Fehlerzustand, eigenes Polling-freies Nachladen nach
 * jeder Aktion. Docked in `DashboardView.tsx` -> `MissionPrimarySurface`
 * (Flaeche "Missionen"), keine neue eigenstaendige Flaeche.
 */

const DECISION_LABELS: Readonly<Record<MissionPlanDecisionKind, string>> = {
  approve: "Plan freigeben",
  reject: "Plan ablehnen",
  request_revision: "Ueberarbeitung anfordern",
  stop: "Mission stoppen",
};

const DECISION_BUSY_LABELS: Readonly<Record<MissionPlanDecisionKind, string>> = {
  approve: "Wird freigegeben…",
  reject: "Wird abgelehnt…",
  request_revision: "Ueberarbeitung wird angefordert…",
  stop: "Wird gestoppt…",
};

/**
 * Deutscher, owner-lesbarer Statustext. Bewusst NIE der englische Rohstatus
 * im sichtbaren Text — Sprache aus dem Auftrag: "Wartet auf Freigabe", nicht
 * "laeuft"; ein rohes `draft` ist fuer den Owner ebenfalls "wartet auf
 * Freigabe", solange keine Entscheidung getroffen wurde.
 */
export function missionStatusLabel(status: string, alreadyDecided: boolean): string {
  if (status === "plan_approved") return "Freigegeben";
  if (status === "plan_rejected") return "Abgelehnt";
  if (status === "revision_requested") return "Zur Ueberarbeitung zurueckgegeben";
  if (status === "stopped") return "Gestoppt";
  // SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 3): eigener, ehrlicher
  // Text fuer den Nach-Freigabe-Stopp (Slice 4) — nicht derselbe wie der
  // Vor-Freigabe-Stopp (Slice 3), da eine Ausfuehrung bereits begonnen haben
  // konnte.
  if (status === "execution_stopped") return "Gestoppt (Ausfuehrung beendet)";
  if (!alreadyDecided) return "Wartet auf Freigabe";
  return "Entschieden";
}

export function missionDecisionErrorCopy(outcome: { status: number; error: string; message?: string }): string {
  // Der Server liefert bei einem Entscheidungskonflikt (409) bereits eine
  // fertige, deutsche, wertfreie Statuszeile ohne Rohfehler — die wird direkt
  // uebernommen, statt eine zweite Uebersetzung zu erfinden.
  if (outcome.message) return outcome.message;
  if (outcome.status === 401) return "Owner-Session ist abgelaufen oder nicht vorhanden. Bitte neu anmelden.";
  if (outcome.status === 403) return "Diese Entscheidung benoetigt die freigegebene Owner-Rolle.";
  if (outcome.status === 404) return "Diese Mission wurde nicht gefunden oder gehoert nicht zu deinem Konto.";
  return "Die Entscheidung konnte nicht angewendet werden. Bitte den aktuellen Stand pruefen und erneut versuchen.";
}

/**
 * SIMO-MISSION-PROGRESS-PROJECTION-1 (B01 Slice 5) — deutscher, owner-lesbarer
 * Text je abgeleitetem Gesamtstatus. Der abgeleitete Status selbst kommt
 * vollstaendig vom Server (`mission-progress-projection.ts`) — dieses Panel
 * uebersetzt ihn nur, es interpretiert nichts neu.
 */
export function missionOverallProgressStatusLabel(status: MissionOverallProgressStatus): string {
  switch (status) {
    case "draft":
      return "Kein Plan vorhanden";
    case "waiting_for_plan_approval":
      return "Wartet auf Plan-Freigabe";
    case "plan_rejected":
      return "Plan abgelehnt";
    case "revision_requested":
      return "Zur Ueberarbeitung zurueckgegeben";
    case "approved":
      return "Freigegeben, noch nicht gestartet";
    case "running":
      return "Laeuft";
    case "waiting_for_owner":
      return "Wartet auf Owner-Freigabe";
    case "recovery_required":
      return "Unterbrochen — braucht eine Owner-Entscheidung";
    case "partially_completed":
      return "Teilweise abgeschlossen";
    case "completed":
      return "Abgeschlossen";
    case "failed":
      return "Fehlgeschlagen";
    case "stopped":
      return "Gestoppt";
    default:
      return "Unbekannter Status";
  }
}

/** Deutscher Text je Schritt-Fortschrittsstatus — jeder Wert bleibt eigenstaendig sichtbar, keiner wird verschwiegen. */
export function missionStepProgressStatusLabel(status: MissionStepProgressStatus): string {
  switch (status) {
    case "stopped":
      return "Gestoppt";
    case "done":
      return "Abgeschlossen";
    case "bind_operation_outcome_unknown":
      return "Bindung: Ausgang unbekannt — braucht Entscheidung";
    case "running":
      return "Laeuft";
    case "awaiting_plan_approval":
      return "Noch nicht gebunden";
    case "waiting_for_owner":
      return "Wartet auf Owner-Freigabe";
    case "recovery_required":
      return "Unterbrochen — braucht Entscheidung";
    case "failed":
      return "Fehlgeschlagen";
    case "retrying":
      return "Wird erneut versucht";
    case "queued":
      return "Eingereiht";
    case "paused":
      return "Pausiert";
    case "archived":
      return "Archiviert";
    default:
      return "Blockiert (Grund nicht erkannt)";
  }
}

export function MissionPlanApprovalPanel(): JSX.Element {
  const [missions, setMissions] = useState<MissionSummaryView[]>([]);
  const [missionsLoading, setMissionsLoading] = useState(false);
  const [missionsError, setMissionsError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [review, setReview] = useState<MissionPlanReviewView | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const [decisionBusy, setDecisionBusy] = useState<MissionPlanDecisionKind | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [decisionInfo, setDecisionInfo] = useState<string | null>(null);

  // SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 1) — "freier Zieltext
  // rein, Mission raus, danach Plan erzeugen".
  //_EMBER_CALM §6: Der Composer öffnet ERST AUF KLICK (kein permanentes
  //_Eingabefeld, das die Missionsfläche dominiert); nach Anlegen schließt er.
  const [composerOffen, setComposerOffen] = useState(false);
  const [newGoal, setNewGoal] = useState("");
  const [newScope, setNewScope] = useState("");
  const [newOutOfScope, setNewOutOfScope] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createInfo, setCreateInfo] = useState<string | null>(null);

  // SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 2) — Freitext-Begruendung
  // fuer "Ueberarbeitung anfordern", Pflichtfeld.
  const [revisionNote, setRevisionNote] = useState("");

  // SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 3) — vereinheitlichter
  // Stop-Knopf (trifft serverseitig selbst den richtigen Pfad, vor oder nach
  // der Freigabe).
  const [stopBusy, setStopBusy] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);
  const [stopInfo, setStopInfo] = useState<string | null>(null);

  // SIMO-MISSION-PROGRESS-PROJECTION-1 (B01 Slice 5) — eigener, unabhaengiger
  // Ladezustand: der Fortschritt ist eine rein abgeleitete Ansicht und laedt
  // unabhaengig vom Review-Zustand (auch VOR einer Entscheidung sichtbar,
  // damit "kein Plan vorhanden"/"wartet auf Freigabe" ebenfalls ehrlich
  // gezeigt wird statt einer leeren Flaeche).
  const [progress, setProgress] = useState<MissionProgressView | null>(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError, setProgressError] = useState<string | null>(null);

  const loadMissions = useCallback(async (): Promise<void> => {
    setMissionsLoading(true);
    try {
      const result = await api.missionPlans();
      setMissions(result.missions);
      setMissionsError(null);
    } catch {
      setMissionsError("Missionsliste konnte nicht geladen werden. Bitte erneut versuchen.");
    } finally {
      setMissionsLoading(false);
    }
  }, []);

  const loadReview = useCallback(async (missionId: string): Promise<void> => {
    setReviewLoading(true);
    try {
      const result = await api.missionPlanReview(missionId);
      setReview(result.review);
      setReviewError(null);
    } catch {
      setReview(null);
      setReviewError("Der Missions-Plan konnte nicht geladen werden. Bitte erneut versuchen.");
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMissions();
  }, [loadMissions]);

  const loadProgress = useCallback(async (missionId: string): Promise<void> => {
    setProgressLoading(true);
    try {
      const result = await api.missionProgress(missionId);
      setProgress(result.progress);
      setProgressError(null);
    } catch {
      setProgress(null);
      setProgressError("Der Missionsfortschritt konnte nicht geladen werden. Bitte erneut versuchen.");
    } finally {
      setProgressLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setReview(null);
      setProgress(null);
      return;
    }
    void loadReview(selectedId);
    void loadProgress(selectedId);
    setRevisionNote("");
    setStopError(null);
    setStopInfo(null);
  }, [selectedId, loadReview, loadProgress]);

  async function handleCreateMission(): Promise<void> {
    const goal = newGoal.trim();
    if (!goal || createBusy) return;
    setCreateBusy(true);
    setCreateError(null);
    setCreateInfo(null);
    try {
      const outcome = await api.missionCreate(goal, {
        scope: newScope.trim() || undefined,
        outOfScope: newOutOfScope.trim() || undefined,
      });
      if (outcome.ok) {
        setCreateInfo(
          outcome.plan.stepCount > 0
            ? `Mission angelegt und geplant (${outcome.plan.stepCount} ${outcome.plan.stepCount === 1 ? "Schritt" : "Schritte"}).`
            : "Mission angelegt.",
        );
        setNewGoal("");
        setNewScope("");
        setNewOutOfScope("");
        await loadMissions();
        setSelectedId(outcome.mission.id);
      } else {
        setCreateError(
          outcome.error === "goal_required"
            ? "Bitte ein Missionsziel eingeben."
            : "Die Mission konnte nicht angelegt werden. Bitte erneut versuchen.",
        );
      }
    } catch {
      setCreateError("Die Mission konnte nicht angelegt werden. Bitte erneut versuchen.");
    } finally {
      setCreateBusy(false);
    }
  }

  async function decide(kind: MissionPlanDecisionKind): Promise<void> {
    if (!selectedId || !review || decisionBusy) return;
    if (kind === "request_revision" && !revisionNote.trim()) return;
    setDecisionBusy(kind);
    setDecisionError(null);
    setDecisionInfo(null);
    try {
      const outcome = await api.missionPlanDecide(
        selectedId,
        kind,
        review.mission.missionVersion,
        kind === "request_revision" ? revisionNote.trim() : undefined,
      );
      if (outcome.ok) {
        setDecisionInfo(`Entscheidung angewendet: ${DECISION_LABELS[kind]}.`);
        if (kind === "request_revision") setRevisionNote("");
      } else {
        setDecisionError(missionDecisionErrorCopy(outcome));
      }
      // In jedem Fall (Erfolg oder Konflikt) den tatsaechlichen Serverstand
      // neu laden — nie auf einer lokalen Annahme weiterarbeiten. Der
      // Fortschritt ist von `missions.status` mit abgeleitet und muss
      // deshalb ebenfalls neu geladen werden.
      await Promise.all([loadReview(selectedId), loadMissions(), loadProgress(selectedId)]);
    } catch {
      setDecisionError("Die Entscheidung konnte nicht angewendet werden. Bitte den aktuellen Stand pruefen und erneut versuchen.");
    } finally {
      setDecisionBusy(null);
    }
  }

  /**
   * SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 3) — EIN Stop-Knopf, der
   * serverseitig selbst den richtigen Pfad waehlt (vor oder nach der
   * Freigabe). Kein separater "Ausfuehrung stoppen"-Knopf noetig.
   */
  async function handleStopMission(): Promise<void> {
    if (!selectedId || !review || stopBusy) return;
    setStopBusy(true);
    setStopError(null);
    setStopInfo(null);
    try {
      const outcome = await api.missionStop(selectedId, review.mission.missionVersion);
      if (outcome.ok) {
        setStopInfo(outcome.path === "post_approval" ? "Die laufende Mission wurde gestoppt." : "Die Mission wurde gestoppt.");
      } else {
        setStopError(outcome.message ?? "Die Mission konnte nicht gestoppt werden. Bitte den aktuellen Stand pruefen und erneut versuchen.");
      }
      await Promise.all([loadReview(selectedId), loadMissions(), loadProgress(selectedId)]);
    } catch {
      setStopError("Die Mission konnte nicht gestoppt werden. Bitte den aktuellen Stand pruefen und erneut versuchen.");
    } finally {
      setStopBusy(false);
    }
  }

  const alreadyDecided = review?.decision.alreadyDecided ?? false;
  // SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 3) — der Stop-Knopf ist
  // sichtbar, solange ueberhaupt noch etwas zu stoppen sein KOENNTE: vor
  // jeder Entscheidung, oder nach einer Freigabe. Bei jedem anderen bereits
  // entschiedenen (terminalen) Status gibt es nichts mehr zu stoppen.
  const canStopMission = review ? !alreadyDecided || review.mission.status === "plan_approved" : false;

  return (
    <section className="card space-y-4 p-4" aria-label="Mission-Plan pruefen und entscheiden" data-mission-plan-approval-panel>
      <div>
        <h2 className="text-sm font-semibold">Mission-Plan pruefen</h2>
        <p className="mt-1 text-xs text-jarvis-muted">
          Hier siehst du, was SIMO als Naechstes vorschlaegt, bevor irgendetwas passiert. Nichts startet von selbst.
        </p>
      </div>

      {/* EMBER_CALM §6 — „+ Neue Mission": Composer auf Abruf. */}
      <div className="space-y-2">
        <button
          type="button"
          className="btn min-h-11 px-3 text-xs"
          aria-expanded={composerOffen}
          aria-controls="mission-composer"
          onClick={() => setComposerOffen((o) => !o)}
          data-testid="mission-composer-toggle"
        >
          {composerOffen ? "Neue Mission schließen" : "+ Neue Mission"}
        </button>
        {composerOffen && (
        <div className="space-y-2 rounded-md border border-jarvis-border/40 bg-jarvis-bg/10 p-3" data-mission-create-form id="mission-composer">
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Neue Mission</h3>
        <label className="block space-y-1">
          <span className="text-[11px] text-jarvis-muted">Ziel (Pflichtfeld)</span>
          <textarea
            value={newGoal}
            onChange={(e) => setNewGoal(e.target.value)}
            placeholder="Was soll SIMO erreichen? Freier Text."
            rows={2}
            className="w-full min-h-11 rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 p-2 text-[13px] text-jarvis-text focus:outline-none focus:ring-2 focus:ring-jarvis-accent/60"
          />
        </label>
        <details className="text-[11px] text-jarvis-muted">
          <summary className="cursor-pointer text-jarvis-text">Scope / Nicht-Scope (optional)</summary>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <label className="block space-y-1">
              <span>Scope</span>
              <input
                value={newScope}
                onChange={(e) => setNewScope(e.target.value)}
                className="w-full min-h-11 rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 p-2 text-[13px] text-jarvis-text focus:outline-none focus:ring-2 focus:ring-jarvis-accent/60"
              />
            </label>
            <label className="block space-y-1">
              <span>Nicht-Scope</span>
              <input
                value={newOutOfScope}
                onChange={(e) => setNewOutOfScope(e.target.value)}
                className="w-full min-h-11 rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 p-2 text-[13px] text-jarvis-text focus:outline-none focus:ring-2 focus:ring-jarvis-accent/60"
              />
            </label>
          </div>
        </details>
        <button
          type="button"
          onClick={() => void handleCreateMission()}
          disabled={createBusy || !newGoal.trim()}
          className="min-h-11 w-full rounded-md border border-jarvis-accent/50 px-3 py-1.5 text-sm font-medium text-jarvis-accent disabled:opacity-50 sm:w-auto"
        >
          {createBusy ? "Wird angelegt und geplant…" : "Mission anlegen"}
        </button>
        {createInfo && <p className="text-[12px] text-emerald-300">{createInfo}</p>}
        {createError && <p className="text-[12px] text-rose-300" role="alert">{createError}</p>}
        </div>
        )}
      </div>

      {/* Missionsliste */}
      <div className="space-y-1">
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Missionen</h3>
        {missionsLoading && missions.length === 0 ? (
          <p className="text-[12px] text-jarvis-muted">Wird geladen …</p>
        ) : missions.length === 0 ? (
          <p className="text-[12px] text-jarvis-muted">Noch keine Mission vorhanden.</p>
        ) : (
          <ul className="space-y-1">
            {missions.map((mission) => (
              <li key={mission.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(mission.id)}
                  aria-pressed={mission.id === selectedId}
                  className={`min-h-11 w-full break-words rounded-md border px-2 py-1.5 text-left text-[12px] ${
                    mission.id === selectedId
                      ? "border-jarvis-accent/60 bg-jarvis-accent/10"
                      : "border-jarvis-border/45 bg-jarvis-bg/25"
                  }`}
                >
                  <span className="block truncate font-medium text-jarvis-text">{mission.goal}</span>
                  <span className="text-jarvis-muted">{missionStatusLabel(mission.status, mission.status !== "draft")}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {missionsError && <p className="text-[12px] text-rose-300">{missionsError}</p>}
      </div>

      {/* Review-Detail */}
      {selectedId && (
        <div className="space-y-3 border-t border-jarvis-border/40 pt-3">
          {reviewLoading && !review ? (
            <p className="text-[12px] text-jarvis-muted">Plan wird geladen …</p>
          ) : reviewError ? (
            <p className="text-[12px] text-rose-300">{reviewError}</p>
          ) : review ? (
            <div className="space-y-3 break-words">
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-accent">Missionsziel</p>
                <p className="text-sm text-jarvis-text">{review.mission.goal}</p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Scope</p>
                  <p className="text-[12px] text-jarvis-text">{review.mission.scope || "Nicht angegeben."}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Nicht-Scope</p>
                  <p className="text-[12px] text-jarvis-text">{review.mission.outOfScope || "Nicht angegeben."}</p>
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Status</p>
                <p className="text-[12px] text-jarvis-text" data-mission-status={review.mission.status}>
                  {missionStatusLabel(review.mission.status, review.decision.alreadyDecided)}
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Voraussichtliche Schritte</p>
                {!review.planExists ? (
                  <p className="text-[12px] text-jarvis-muted">Fuer diese Mission wurde noch kein Plan erzeugt.</p>
                ) : (
                  <ol className="mt-1 space-y-2">
                    {review.steps.map((step) => (
                      <li key={step.id} className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/20 p-2">
                        <p className="text-[12px] font-medium text-jarvis-text">
                          {step.order}. {step.title}
                        </p>
                        <p className="mt-0.5 text-[12px] text-jarvis-muted">{step.goal}</p>
                        {step.rationale && (
                          <p className="mt-1 text-[11px] text-jarvis-accent">Begruendung: {step.rationale}</p>
                        )}
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-jarvis-muted">
                          <span>Betroffenes Projekt/Repo: {step.productScope || "Nicht angegeben."}</span>
                          <span>
                            Benoetigte Capabilities:{" "}
                            {step.requiredCapabilities.length > 0 ? step.requiredCapabilities.join(", ") : "Keine explizit benannt."}
                          </span>
                          <span>
                            Erkennbare Owner-Gates:{" "}
                            {step.approvalsNeeded.length > 0 ? step.approvalsNeeded.join(", ") : "Keine explizit vermerkt."}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Ehrliche Luecken — was das Datenmodell heute nicht erfasst. */}
              <details className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/10 p-2 text-[11px] text-jarvis-muted">
                <summary className="cursor-pointer text-jarvis-text">Risiko, erwartete Nachweise, offene Annahmen</summary>
                <div className="mt-2 space-y-1.5">
                  <p><strong className="text-jarvis-text">Risiko:</strong> {review.notCapturedByModel.risk}</p>
                  <p><strong className="text-jarvis-text">Erwartete Nachweise:</strong> {review.notCapturedByModel.expectedEvidence}</p>
                  <p><strong className="text-jarvis-text">Offene Annahmen und Unsicherheiten:</strong> {review.notCapturedByModel.openAssumptions}</p>
                </div>
              </details>

              {/* Technische Details oeffnen — Aktion aus dem Auftrag. */}
              <details className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/10 p-2 text-[11px] text-jarvis-muted">
                <summary className="cursor-pointer text-jarvis-text">Technische Details oeffnen</summary>
                <div className="mt-2 space-y-1 font-mono text-[10px] break-all">
                  <p>Mission-ID: {review.mission.id}</p>
                  <p>Version: {review.mission.missionVersion}</p>
                  <p>Planversion: {review.mission.planVersion}</p>
                  <p>Angelegt: {review.mission.createdAt}</p>
                  <p>Zuletzt geaendert: {review.mission.updatedAt}</p>
                </div>
              </details>

              {/* SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 2) —
                  Ueberarbeitungs-Historie: die alte Planversion verschwindet
                  nicht, sie bleibt hier lesbar nachvollziehbar. */}
              {review.revisionHistory.length > 0 && (
                <details className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/10 p-2 text-[11px] text-jarvis-muted" data-mission-revision-history>
                  <summary className="cursor-pointer text-jarvis-text">
                    Ueberarbeitungs-Historie ({review.revisionHistory.length})
                  </summary>
                  <ol className="mt-2 space-y-2">
                    {review.revisionHistory.map((entry, index) => (
                      <li key={`${entry.createdAt}-${index}`} className="rounded border border-jarvis-border/30 bg-black/10 p-1.5">
                        <p className="text-jarvis-text">
                          Planversion {entry.planVersionBefore} → {entry.planVersionAfter}
                        </p>
                        <p className="mt-0.5">{entry.note}</p>
                        <p className="mt-0.5 font-mono text-[10px]">{entry.createdAt}</p>
                      </li>
                    ))}
                  </ol>
                </details>
              )}

              {/* Entscheidungen — keine "Alles automatisch erlauben"-Schaltflaeche. */}
              {alreadyDecided && review.mission.status !== "plan_approved" ? (
                <p className="text-[12px] text-jarvis-muted">
                  Diese Mission ist bereits entschieden ({missionStatusLabel(review.mission.status, true)}). Eine weitere Entscheidung ist nicht mehr moeglich.
                </p>
              ) : !alreadyDecided ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Mission-Plan Entscheidung">
                    {(["approve", "reject", "request_revision"] as MissionPlanDecisionKind[]).map((kind) => (
                      <button
                        key={kind}
                        type="button"
                        onClick={() => void decide(kind)}
                        disabled={decisionBusy !== null || (kind === "request_revision" && !revisionNote.trim())}
                        className={`min-h-11 inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${
                          kind === "approve"
                            ? "border-emerald-400/50 text-emerald-200"
                            : kind === "reject"
                              ? "border-rose-400/50 text-rose-200"
                              : "border-jarvis-border/60"
                        }`}
                      >
                        {decisionBusy === kind ? DECISION_BUSY_LABELS[kind] : DECISION_LABELS[kind]}
                      </button>
                    ))}
                  </div>
                  {/* SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 2) — die
                      Begruendung ist Pflicht, damit "Ueberarbeitung anfordern"
                      nicht mehr ins Leere fuehrt. */}
                  <label className="block space-y-1">
                    <span className="text-[11px] text-jarvis-muted">
                      Begruendung fuer eine Ueberarbeitung (Pflicht fuer "Ueberarbeitung anfordern")
                    </span>
                    <textarea
                      value={revisionNote}
                      onChange={(e) => setRevisionNote(e.target.value)}
                      rows={2}
                      placeholder="Was soll am Plan anders sein?"
                      className="w-full min-h-11 rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 p-2 text-[13px] text-jarvis-text focus:outline-none focus:ring-2 focus:ring-jarvis-accent/60"
                    />
                  </label>
                </div>
              ) : null}
              {decisionInfo && <p className="text-[12px] text-emerald-300">{decisionInfo}</p>}
              {decisionError && <p className="text-[12px] text-rose-300" role="alert">{decisionError}</p>}

              {/* SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 3) —
                  vereinheitlichter Stop-Knopf: sichtbar vor JEDER Entscheidung
                  und nach einer Freigabe, trifft serverseitig selbst den
                  richtigen der zwei bestehenden Stop-Pfade. */}
              {canStopMission && (
                <div className="border-t border-jarvis-border/40 pt-3" data-mission-stop-control>
                  <button
                    type="button"
                    onClick={() => void handleStopMission()}
                    disabled={stopBusy}
                    className="min-h-11 inline-flex items-center justify-center rounded-md border border-rose-400/50 px-3 py-1.5 text-sm font-medium text-rose-200 disabled:opacity-50"
                  >
                    {stopBusy ? "Wird gestoppt…" : "Mission stoppen"}
                  </button>
                  {stopInfo && <p className="mt-1 text-[12px] text-emerald-300">{stopInfo}</p>}
                  {stopError && <p className="mt-1 text-[12px] text-rose-300" role="alert">{stopError}</p>}
                </div>
              )}

              {/* SIMO-MISSION-PROGRESS-PROJECTION-1 (B01 Slice 5) — Fortschritt
                  wird ausschliesslich abgeleitet, nie manuell gepflegt. Eigener
                  Abschnitt, bestehende Review-/Entscheidungsinhalte oben bleiben
                  unveraendert. */}
              <div className="space-y-3 border-t border-jarvis-border/40 pt-3" data-mission-progress-section>
                <h3 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">Fortschritt</h3>
                {progressLoading && !progress ? (
                  <p className="text-[12px] text-jarvis-muted">Fortschritt wird geladen …</p>
                ) : progressError ? (
                  <p className="text-[12px] text-rose-300">{progressError}</p>
                ) : progress ? (
                  <div className="space-y-3 break-words">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Gesamtzustand</p>
                      <p className="text-sm text-jarvis-text" data-mission-progress-status={progress.overallStatus}>
                        {missionOverallProgressStatusLabel(progress.overallStatus)}
                      </p>
                      {progress.counts.total > 0 && (
                        <p className="mt-0.5 text-[12px] text-jarvis-muted">
                          {progress.counts.done} von {progress.counts.total} Schritten abgeschlossen
                          {progress.counts.failed > 0 ? `, ${progress.counts.failed} fehlgeschlagen` : ""}
                          {progress.counts.stopped > 0 ? `, ${progress.counts.stopped} gestoppt` : ""}.
                        </p>
                      )}
                    </div>

                    {/* Naechster sicherer Schritt — nie erfunden, nur wenn der Server einen liefert. */}
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-accent">Naechster sicherer Schritt</p>
                      {progress.nextSafeStep ? (
                        <div className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/20 p-2">
                          <p className="text-[12px] font-medium text-jarvis-text">{progress.nextSafeStep.label}</p>
                          <p className="mt-0.5 text-[12px] text-jarvis-muted">{progress.nextSafeStep.reason}</p>
                        </div>
                      ) : (
                        <p className="text-[12px] text-jarvis-muted">
                          Aktuell gibt es keinen weiteren sicheren Schritt (z.B. laeuft bereits etwas oder die Mission ist abgeschlossen/beendet).
                        </p>
                      )}
                    </div>

                    {/* Schrittliste mit Status, Evidence und zugehoerigem Run. */}
                    {progress.steps.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Schritte</p>
                        <ol className="mt-1 space-y-2">
                          {progress.steps.map((step) => (
                            <li key={step.id} className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/20 p-2" data-mission-step-progress={step.progressStatus}>
                              <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                                <p className="text-[12px] font-medium text-jarvis-text">
                                  {step.order}. {step.title}
                                </p>
                                <span className="text-[11px] text-jarvis-muted">{missionStepProgressStatusLabel(step.progressStatus)}</span>
                              </div>
                              {step.blockingStepId && (
                                <p className="mt-0.5 text-[11px] text-jarvis-muted">
                                  Wartet auf Abschluss des vorherigen Schritts, bevor er sicher gebunden werden kann.
                                </p>
                              )}
                              {step.run && (
                                <p className="mt-0.5 text-[11px] text-jarvis-muted">
                                  Zugehoeriger Lauf: {step.run.status}
                                  {step.run.finalAnswer ? ` — ${step.run.finalAnswer}` : ""}
                                </p>
                              )}
                              {step.evidence.length > 0 && (
                                <details className="mt-1 text-[11px] text-jarvis-muted">
                                  <summary className="cursor-pointer text-jarvis-text">Evidence ({step.evidence.length})</summary>
                                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded border border-jarvis-border/30 bg-black/20 p-1.5 font-mono text-[10px]">
                                    {JSON.stringify(step.evidence, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Ehrliche Luecke — zugehoerige Commits/Pushes/PRs, aktuell meist leer. */}
                    <details className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/10 p-2 text-[11px] text-jarvis-muted">
                      <summary className="cursor-pointer text-jarvis-text">
                        Zugehoerige Commits/Pushes/PRs ({progress.relatedProviderLaneRuns.length})
                      </summary>
                      <div className="mt-2 space-y-1.5">
                        {progress.relatedProviderLaneRuns.length === 0 ? (
                          <p>{progress.notCapturedByModel.commitsPushesPullRequests}</p>
                        ) : (
                          <ul className="space-y-1">
                            {progress.relatedProviderLaneRuns.map((run) => (
                              <li key={run.runId} className="break-all">
                                {run.branch} — {run.state}
                                {run.pullRequestUrl ? ` — ${run.pullRequestUrl}` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </details>

                    {/* Technische Details — Roh-Missionsstatus (zur Unterscheidung der zwei Stopp-Quellen). */}
                    <details className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/10 p-2 text-[11px] text-jarvis-muted">
                      <summary className="cursor-pointer text-jarvis-text">Technische Fortschritts-Details</summary>
                      <div className="mt-2 space-y-1 font-mono text-[10px] break-all">
                        <p>Roh-Missionsstatus: {progress.mission.statusRaw}</p>
                        <p>Version: {progress.mission.missionVersion}</p>
                        <p>
                          Zaehler: done={progress.counts.done} failed={progress.counts.failed} active={progress.counts.active} wartet_auf_owner=
                          {progress.counts.waitingForOwner} braucht_wiederherstellung={progress.counts.recoveryRequired} gestoppt=
                          {progress.counts.stopped} nicht_gestartet={progress.counts.notStarted}
                        </p>
                      </div>
                    </details>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
